// src/utils/resolvedEvents.ts
import type { Event } from "../types/events";
import type { ResolvedEvent } from "../types/resolvedEvents";
import { toDate } from "./dateUtils";

/**
 * Resolves an Event with DateInput to ResolvedEvent with actual Date objects
 */
export const resolveEventDates = (event: Event): ResolvedEvent => {
  const startDate = toDate(event.startDate);
  const endDate = event.endDate ? toDate(event.endDate) || undefined : undefined;

  // Fallback to current date if startDate is invalid
  const resolvedStartDate = startDate || new Date();

  return {
    ...event,
    startDate: resolvedStartDate,
    endDate,
  };
};

/**
 * Resolves an array of events to ResolvedEvent[]
 */
export const resolveEvents = (events: Event[]): ResolvedEvent[] => {
  return events.map(resolveEventDates);
};

/**
 * Utility functions for working with resolved events
 */
export const ResolvedEventUtils = {
  /**
   * Check if event is in the past
   */
  isPast: (event: ResolvedEvent): boolean => {
    return event.startDate < new Date();
  },

  /**
   * Check if event is upcoming (starts today or later)
   */
  isUpcoming: (event: ResolvedEvent): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return event.startDate >= today;
  },

  /**
   * Get days until event (negative if past)
   */
  getDaysUntil: (event: ResolvedEvent): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(event.startDate);
    eventDate.setHours(0, 0, 0, 0);

    const diffTime = eventDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },

  /**
   * Format event date with consistent formatting
   */
  formatDate: (
    event: ResolvedEvent,
    options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  ): string => {
    return event.startDate.toLocaleDateString("en-US", options);
  },

  /**
   * Format date range for events with end dates
   */
  formatDateRange: (
    event: ResolvedEvent,
    options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  ): string => {
    if (!event.endDate || event.startDate.getTime() === event.endDate.getTime()) {
      return event.startDate.toLocaleDateString("en-US", options);
    }

    // Same month and year - format as "Mar 15-20, 2024"
    if (
      event.startDate.getMonth() === event.endDate.getMonth() &&
      event.startDate.getFullYear() === event.endDate.getFullYear()
    ) {
      const monthYear = event.startDate.toLocaleDateString("en-US", {
        month: options.month || "short",
        year: options.year || "numeric",
      });
      return `${monthYear.replace(/\d+,?\s*\d*/, "")}${event.startDate.getDate()}-${event.endDate.getDate()}, ${event.startDate.getFullYear()}`;
    }

    // Different months or years
    return `${event.startDate.toLocaleDateString("en-US", options)} - ${event.endDate.toLocaleDateString("en-US", options)}`;
  },

  /**
   * Sort events by start date (earliest first)
   */
  sortByDate: (events: ResolvedEvent[]): ResolvedEvent[] => {
    return [...events].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  },

  /**
   * Filter events by date range
   */
  filterByDateRange: (
    events: ResolvedEvent[],
    startDate?: Date,
    endDate?: Date
  ): ResolvedEvent[] => {
    return events.filter(event => {
      if (startDate && event.startDate < startDate) return false;
      if (endDate && event.startDate > endDate) return false;
      return true;
    });
  },
};
